/**
 * Client-side error beacon. Catches:
 *
 *   - Render crashes (via ErrorBoundary.componentDidCatch, which calls report()
 *     directly — wired in components/ErrorBoundary.tsx).
 *   - Uncaught script errors anywhere on the page (window.onerror).
 *   - Unhandled promise rejections (window.unhandledrejection).
 *
 * Forwards a minimal payload to /api/client-error which routes to the team
 * inbox via the same server-side reporter (dedupe + redaction applied there).
 * Local throttle just keeps us from spamming the network on a runaway loop.
 *
 * Best-effort everything — failures inside the reporter never bubble up to
 * the user. A broken reporter must not break the app.
 */
import axios from 'axios';

const LOCAL_COOLDOWN_MS = 30_000; // 30s between identical client beacons
const lastSentBySig = new Map<string, number>();
const MAX_KEYS = 50;

// Known third-party noise (e.g. the Outlook SafeLinks scanner) is NOT dropped
// here — the server beacon route (routes/clientError.ts KNOWN_NOISE) labels
// it instead, because the user wants to keep seeing those reports for now.
// Server-side is also the only gate stale cached bundles respect.

function sigKey(message: string, stack: string | undefined, source: string): string {
  const stackHead = (stack || '').split('\n').slice(0, 2).join('\n');
  return `${source}::${message}::${stackHead}`.slice(0, 500);
}

export function reportClientError(input: {
  message: string;
  stack?: string;
  source: 'ErrorBoundary' | 'window.onerror' | 'unhandledrejection';
  url?: string;
}): void {
  try {
    if (!input.message) return;
    const sig = sigKey(input.message, input.stack, input.source);
    const now = Date.now();
    const last = lastSentBySig.get(sig);
    if (last && now - last < LOCAL_COOLDOWN_MS) return;

    if (lastSentBySig.size >= MAX_KEYS && !last) {
      const oldest = [...lastSentBySig.entries()].sort((a, b) => a[1] - b[1])[0]?.[0];
      if (oldest) lastSentBySig.delete(oldest);
    }
    lastSentBySig.set(sig, now);

    // Pull user/org from localStorage if present — useful for triage.
    let userId: string | undefined; let orgId: string | undefined;
    try {
      const u = JSON.parse(localStorage.getItem('erm-user') || 'null');
      userId = u?.id; orgId = u?.orgId;
    } catch { /* localStorage may be unavailable */ }

    // Use axios directly (not the configured `api` instance) so a 401 from the
    // public route can't bounce us to /login mid-error-report.
    axios.post('/api/client-error', {
      message: input.message.slice(0, 2000),
      stack: (input.stack || '').slice(0, 20_000),
      source: input.source,
      url: input.url || (typeof window !== 'undefined' ? window.location.href : undefined),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      userId, orgId,
    }, { timeout: 5_000 }).catch(() => { /* swallow; nothing else to do */ });
  } catch {
    /* swallow — a broken reporter must not break the app */
  }
}

let installed = false;
export function installClientErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    reportClientError({
      message: event.message || 'window.onerror',
      stack: event.error?.stack,
      source: 'window.onerror',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason?.message ?? String(reason);
    reportClientError({
      message: message || 'unhandledrejection',
      stack: reason?.stack,
      source: 'unhandledrejection',
    });
  });
}
